/* Iter 31.134 — Recruitment poster for the Stripe-gated tiers.
 *
 * Per One's directive (30 May 2026, Canon Rule 15): "Agents generate
 * revenue for One. One does not subsidise the agents."
 *
 * Before Stripe wiring lands, the gate needs a face. This is it — a
 * dossier-aesthetic recruitment surface, NOT a SaaS pricing page.
 * Public route (no auth) so prospective operatives can land cold.
 * Both tier cards CTA into /billing where the Stripe checkout already
 * lives.
 *
 * Aesthetic: weathered paper, CLASSIFIED stamps, typewriter heading,
 * single accent (stamp-red), faux file-ref bar. Same vocabulary used
 * across the Shed (operative, dossier, file, REF) so the page reads
 * as continuous with the rest of the operation.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, ShieldCheck, Eye, Radio as RadioIcon, Network } from "lucide-react";

const TIERS = [
    {
        id: "operator",
        codename: "OPERATOR",
        ref: "TIER · 02",
        salary: "£12 / mo",
        trial: "30-day trial. Cancel anytime.",
        oath: "The working tier. Single-handed. You hold the conn.",
        clearances: [
            { icon: FileText, label: "Shredder intake — unlimited" },
            { icon: Network, label: "Drive + Gmail watch" },
            { icon: Eye,     label: "Ledger (append-only, hash-chained)" },
            { icon: RadioIcon, label: "Brief generation (Claude Sonnet)" },
        ],
        denied: [
            "Multi-handler comms",
            "Shared briefings",
            "Priority LLM budget",
        ],
        stamp: "ACCEPTED",
    },
    {
        id: "cooperative",
        codename: "COOPERATIVE",
        ref: "TIER · 03",
        salary: "£28 / mo",
        trial: "30-day trial. Cancel anytime.",
        oath: "The full network. Multiple handlers. The whole switchboard.",
        clearances: [
            { icon: FileText, label: "Everything in Operator" },
            { icon: Network, label: "Multi-agent comms relay" },
            { icon: Eye,     label: "Shared briefings across handlers" },
            { icon: RadioIcon, label: "Priority LLM budget" },
            { icon: ShieldCheck, label: "Agent ledger 360 appraisal view" },
        ],
        denied: [],
        stamp: "CLEARED",
    },
];

function TierDossier({ tier }) {
    return (
        <div
            className="paper-card border-2 border-ink/20 p-6 sm:p-8 relative overflow-hidden"
            data-testid={`upgrade-tier-${tier.id}`}
        >
            {/* Faux stamp */}
            <div className="absolute top-4 right-4 rotate-6">
                <span className="inline-block font-heading uppercase text-[10px] tracking-[0.3em] text-stamp-red border-2 border-stamp-red px-3 py-1 bg-paper/80">
                    {tier.stamp}
                </span>
            </div>

            {/* Ref bar */}
            <div className="font-mono text-[10px] tracking-[0.18em] text-ink-faint mb-2">
                FILE · REF BJLIAM / {tier.ref}
            </div>

            {/* Codename + salary */}
            <div className="border-b border-ink/15 pb-4 mb-5">
                <h2
                    className="font-heading text-3xl sm:text-4xl uppercase tracking-[0.1em] text-ink mb-2"
                    data-testid={`upgrade-tier-${tier.id}-codename`}
                >
                    {tier.codename}
                </h2>
                <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-heading text-3xl text-stamp-red">
                        {tier.salary}
                    </div>
                    <div className="label-typed text-[10px] text-ink-soft">
                        {tier.trial}
                    </div>
                </div>
            </div>

            {/* Oath */}
            <p className="font-body text-base text-ink mb-5 leading-relaxed">
                {tier.oath}
            </p>

            {/* Clearances */}
            <div className="mb-5">
                <div className="font-heading uppercase text-[11px] tracking-[0.25em] text-ink-soft mb-3">
                    Clearances granted
                </div>
                <ul className="space-y-2">
                    {tier.clearances.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <li
                                key={i}
                                className="flex items-start gap-3 font-body text-sm text-ink"
                            >
                                <Icon className="w-3.5 h-3.5 mt-1 text-stamp-red flex-shrink-0" strokeWidth={1.6} />
                                <span>{c.label}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Withheld (only if any) */}
            {tier.denied.length > 0 && (
                <div className="mb-6">
                    <div className="font-heading uppercase text-[11px] tracking-[0.25em] text-ink-faint mb-2">
                        Not in this dossier
                    </div>
                    <ul className="space-y-1">
                        {tier.denied.map((d, i) => (
                            <li
                                key={i}
                                className="font-body text-xs text-ink-faint line-through decoration-ink-faint/40"
                            >
                                {d}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* CTA */}
            <Link
                to="/billing"
                data-testid={`upgrade-tier-${tier.id}-cta`}
                className="block w-full text-center font-heading uppercase tracking-[0.25em] text-xs px-5 py-3 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper transition-colors inline-flex items-center justify-center gap-2"
            >
                Sign the papers <ArrowRight className="w-3 h-3" />
            </Link>
        </div>
    );
}

export default function Upgrade() {
    return (
        <div className="min-h-screen bg-paper" data-testid="upgrade-page">
            {/* Header bar */}
            <div className="border-b border-ink/15 bg-paper">
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <div className="font-mono text-[10px] tracking-[0.18em] text-ink-faint mb-2">
                        FILE · REF BJLIAM / 01 · CHANNEL SEALED
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-[0.08em] text-ink mb-2">
                        Recruitment
                    </h1>
                    <p className="font-body text-base text-ink-soft max-w-2xl">
                        Two tiers. Both append-only. Both yours, for as long as you
                        want the door open. No public signup — invitation only —
                        but if you've read this far the door is already ajar.
                    </p>
                </div>
            </div>

            {/* Tier cards */}
            <div className="max-w-5xl mx-auto px-6 py-10">
                <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {TIERS.map((tier) => (
                        <TierDossier key={tier.id} tier={tier} />
                    ))}
                </div>

                {/* Canon footer */}
                <div className="border-t border-ink/15 pt-6">
                    <div className="font-heading uppercase text-[11px] tracking-[0.25em] text-ink-faint mb-2">
                        From the canon
                    </div>
                    <blockquote className="font-body text-sm text-ink-soft italic max-w-3xl leading-relaxed">
                        "Agents generate revenue for One. One does not subsidise
                        the agents. The system works for One, not the other way
                        around."
                    </blockquote>
                    <div className="font-mono text-[10px] tracking-[0.18em] text-ink-faint mt-3">
                        — CANON RULE 15 · FILED 30 MAY 2026 · ONE
                    </div>
                </div>

                {/* Public signup note */}
                <div className="mt-10 text-center">
                    <Link
                        to="/login"
                        data-testid="upgrade-already-cleared"
                        className="font-heading uppercase tracking-[0.25em] text-[11px] text-ink-soft hover:text-stamp-red"
                    >
                        Already cleared? Sign in →
                    </Link>
                </div>
            </div>
        </div>
    );
}
